import { Vector3, TransformNode, Scene, Mesh, MeshBuilder, Color3, StandardMaterial, VertexData } from "babylonjs";
import { GridMaterial } from "babylonjs-materials";
import { GestureController, GestureOptions } from "./GestureController";
import { ContentInstance, ContentItem } from "../../content/types";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export enum PlacementState {
  IDLE = 'idle',
  PLACING = 'placing',
  PLACED = 'placed',
}

export interface ReticleConfig {
  /** Show reticle during placement (default: true) */
  enabled?: boolean;
  /** Reticle radius in meters (default: 0.15) */
  radius?: number;
  /** Reticle color (default: white) */
  color?: Color3;
  /** Reticle opacity (default: 1) */
  opacity?: number;
  /** Ring thickness as fraction of radius (default: 0.3) */
  thickness?: number;
}

export interface PlacementConfig {
  /** Ground Y offset */
  groundY?: number;
  /** Initial/default placement distance from camera */
  distance?: number;
  /** Minimum placement distance */
  minDistance?: number;
  /** Maximum placement distance */
  maxDistance?: number;
  /** Lerp factor for smooth movement (0-1) */
  lerpFactor?: number;
  /** Gesture options (null to disable) */
  gestures?: GestureOptions | null;
  /** Callback when placement is confirmed */
  onConfirm?: () => void;
  /** Callback when content is repositioned via tap */
  onReposition?: (position: Vector3) => void;
  /** Viewport X for ground raycast (0-1, default 0.5 = center) */
  viewportX?: number;
  /** Viewport Y for ground raycast (0-1, default 0.6 = slightly below center) */
  viewportY?: number;
  /** Show debug ground grid */
  debugGround?: boolean;
  /** Floor reticle configuration */
  reticle?: ReticleConfig;
}

// ─────────────────────────────────────────────────────────────
// PlacementController
// ─────────────────────────────────────────────────────────────

/**
 * PlacementController - Clean, focused AR content placement
 * 
 * Core responsibilities:
 * - State management (idle → placing → placed)
 * - Content positioning with distance clamping
 * - Smooth lerping to target position
 * - Face camera orientation
 * - Gesture handling (via GestureController)
 * 
 * Usage:
 * ```ts
 * const placement = new PlacementController();
 * placement.initialize(scene);
 * placement.startPlacement();
 * 
 * // In render loop - provide position from hit test:
 * placement.setTargetPosition(hitPosition);
 * placement.update();
 * 
 * // On tap to confirm:
 * placement.confirmPlacement();
 * ```
 */
export class PlacementController {
  private scene?: Scene;
  private contentNode?: TransformNode;
  private gestures?: GestureController;
  
  private state = PlacementState.IDLE;
  private targetPosition = new Vector3();
  private hasTarget = false;
  
  // Config with defaults
  private groundY: number;
  private distance: number;
  private minDistance: number;
  private maxDistance: number;
  private lerpFactor: number;
  private viewportX: number;
  private viewportY: number;
  private debugGround: boolean;
  private onConfirm?: () => void;
  private onReposition?: (position: Vector3) => void;
  
  // Virtual ground for raycasting
  private groundMesh?: Mesh;
  
  // Floor reticle
  private reticleMesh?: Mesh;
  private reticleConfig: Required<ReticleConfig>;

  constructor(config?: PlacementConfig) {
    this.groundY = config?.groundY ?? 0;
    this.distance = config?.distance ?? 3;
    this.minDistance = config?.minDistance ?? 0.5;
    this.maxDistance = config?.maxDistance ?? 10;
    this.lerpFactor = config?.lerpFactor ?? 0.15;
    this.viewportX = config?.viewportX ?? 0.5;
    this.viewportY = config?.viewportY ?? 0.5;
    this.debugGround = config?.debugGround ?? false;
    this.onConfirm = config?.onConfirm;
    this.onReposition = config?.onReposition;
    
    // Reticle defaults
    this.reticleConfig = {
      enabled: config?.reticle?.enabled ?? true,
      radius: config?.reticle?.radius ?? 0.15,
      color: config?.reticle?.color ?? new Color3(1, 1, 1),
      opacity: config?.reticle?.opacity ?? 1,
      thickness: config?.reticle?.thickness ?? 0.3,
    };
    
    if (config?.gestures !== null) {
      this.gestures = new GestureController(config?.gestures);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Initialization
  // ─────────────────────────────────────────────────────────────

  public initialize(scene: Scene, contentNode?: TransformNode): void {
    this.scene = scene;
    this.contentNode = contentNode ?? new TransformNode('placementAnchor', scene);
    
    if (this.gestures) {
      this.gestures.initialize(scene, this.contentNode);
    }
    
    // Create ground mesh for raycasting
    if (!this.groundMesh) {
      this.groundMesh = MeshBuilder.CreatePlane("placementGround", { size: 2000 }, scene);
      this.groundMesh.rotation.x = Math.PI / 2;
      this.groundMesh.position.y = this.groundY;
      this.groundMesh.isPickable = true;
      
      const mat = new GridMaterial("placementGroundMat", scene);
      mat.opacity = this.debugGround ? 0.3 : 0;
      mat.mainColor = new Color3(0.2, 0.2, 0.3);
      mat.lineColor = new Color3(0, 0.7, 1);
      this.groundMesh.material = mat;
    }
    
    // Create floor reticle
    if (this.reticleConfig.enabled && !this.reticleMesh) {
      this.createReticle(scene);
    }
  }
  
  private createReticle(scene: Scene): void {
    const { radius, color, opacity, thickness } = this.reticleConfig;
    
    // Create flat 2D ring (annulus) - donut shape
    this.reticleMesh = this.createAnnulus(scene, radius, radius * (1 - thickness), 64);
    this.reticleMesh.position.y = this.groundY + 0.001;
    
    // Material - fully emissive so it's bright regardless of lighting
    const mat = new StandardMaterial("placementReticleMat", scene);
    mat.diffuseColor = new Color3(0, 0, 0);
    mat.emissiveColor = color;
    mat.alpha = opacity;
    mat.backFaceCulling = false;
    mat.disableLighting = true;
    this.reticleMesh.material = mat;
    
    // Start hidden
    this.reticleMesh.setEnabled(false);
  }
  
  /** Create a flat 2D ring (annulus) mesh */
  private createAnnulus(scene: Scene, outerRadius: number, innerRadius: number, tessellation: number): Mesh {
    const positions: number[] = [];
    const indices: number[] = [];
    const normals: number[] = [];
    
    // Create vertices for inner and outer circles
    for (let i = 0; i <= tessellation; i++) {
      const angle = (i / tessellation) * Math.PI * 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      
      // Outer vertex
      positions.push(cos * outerRadius, 0, sin * outerRadius);
      normals.push(0, 1, 0);
      
      // Inner vertex
      positions.push(cos * innerRadius, 0, sin * innerRadius);
      normals.push(0, 1, 0);
    }
    
    // Create triangles
    for (let i = 0; i < tessellation; i++) {
      const outerCurrent = i * 2;
      const innerCurrent = i * 2 + 1;
      const outerNext = (i + 1) * 2;
      const innerNext = (i + 1) * 2 + 1;
      
      // Two triangles per segment
      indices.push(outerCurrent, outerNext, innerCurrent);
      indices.push(innerCurrent, outerNext, innerNext);
    }
    
    const mesh = new Mesh("placementReticle", scene);
    const vertexData = new VertexData();
    vertexData.positions = positions;
    vertexData.indices = indices;
    vertexData.normals = normals;
    vertexData.applyToMesh(mesh);
    
    return mesh;
  }

  // ─────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────

  public startPlacement(): void {
    if (this.state === PlacementState.PLACING) return;
    
    const wasPlaced = this.state === PlacementState.PLACED;
    this.state = PlacementState.PLACING;
    this.gestures?.disable();
    
    // Show reticle
    this.reticleMesh?.setEnabled(true);
    
    if (wasPlaced && this.contentNode) {
      // Repositioning: keep current position, start tracking from there
      this.targetPosition.copyFrom(this.contentNode.position);
      this.hasTarget = true;
    } else {
      // First placement: start with content in front of camera
      this.hasTarget = false;
      this.placeInFrontOfCamera();
    }
  }

  public confirmPlacement(): void {
    if (this.state !== PlacementState.PLACING) return;
    
    this.state = PlacementState.PLACED;
    // Hide reticle
    this.reticleMesh?.setEnabled(false);
    // Enable gestures on interaction target (falls back to canvas in GestureController)
    this.gestures?.enable(this.interactionTarget);
    // Notify caller
    this.onConfirm?.();
  }

  public reset(): void {
    this.state = PlacementState.IDLE;
    this.hasTarget = false;
    this.gestures?.disable();
    this.gestures?.resetState();
    // Hide reticle
    this.reticleMesh?.setEnabled(false);
  }

  /** Re-enter placement mode while keeping current position */
  public reposition(): void {
    if (this.state !== PlacementState.PLACED) return;
    
    this.state = PlacementState.PLACING;
    this.gestures?.disable();
    
    // Show reticle
    this.reticleMesh?.setEnabled(true);
    
    // Keep current position - don't reset to camera
    if (this.contentNode) {
      this.targetPosition.copyFrom(this.contentNode.position);
      this.hasTarget = true;
    }
  }

  /**
   * Show or hide the placement reticle.
   * Use this to defer reticle visibility until content has loaded.
   */
  public setReticleVisible(visible: boolean): void {
    if (this.reticleConfig.enabled) {
      this.reticleMesh?.setEnabled(visible);
    }
  }

  /**
   * Set callback for tap events (delegates to GestureController)
   * Tap is detected when finger moves < 15px between down and up
   */
  public setOnTap(callback: (screenX: number, screenY: number) => void): void {
    this.gestures?.setOnTap(callback);
  }

  // ─────────────────────────────────────────────────────────────
  // Position Updates
  // ─────────────────────────────────────────────────────────────

  /**
   * Set target position from hit test result
   * Call this each frame with your hit test position
   */
  public setTargetPosition(position: Vector3, yOffset = 0): void {
    if (this.state !== PlacementState.PLACING) return;
    
    this.targetPosition.copyFrom(position);
    this.targetPosition.y += yOffset;
    this.hasTarget = true;
  }

  /**
   * Update - call each frame during placement
   * Raycasts to ground and lerps content toward hit position
   */
  public update(): void {
    if (this.state !== PlacementState.PLACING) return;
    if (!this.contentNode || !this.scene) return;
    
    const camera = this.scene.activeCamera;
    if (!camera) return;
    
    // Raycast to ground
    const hitPos = this.raycastToGround();
    
    if (hitPos) {
      this.targetPosition.copyFrom(hitPos);
      this.hasTarget = true;
    } else {
      // Horizon fallback: keep current distance, follow viewport direction.
      const dirXZ = this.getViewportDirectionXZ();
      if (dirXZ && this.hasTarget) {
        const camXZ = new Vector3(camera.position.x, 0, camera.position.z);
        const currentVec = new Vector3(
          this.targetPosition.x - camXZ.x,
          0,
          this.targetPosition.z - camXZ.z
        );
        const radius = Math.max(
          this.minDistance,
          Math.min(this.maxDistance, currentVec.length())
        );

        // Keep fallback in the same hemisphere to avoid behind-camera flips.
        if (currentVec.lengthSquared() > 0.0001 && Vector3.Dot(dirXZ, currentVec) < 0) {
          dirXZ.scaleInPlace(-1);
        }

        this.targetPosition.set(
          camXZ.x + dirXZ.x * radius,
          this.groundY,
          camXZ.z + dirXZ.z * radius
        );
      }
    }
    
    if (!this.hasTarget) return;
    
    // Clamp distance from camera
    const clamped = this.clampDistance(this.targetPosition);
    
    // Lerp to target
    const pos = this.contentNode.position;
    pos.x += (clamped.x - pos.x) * this.lerpFactor;
    pos.y += (clamped.y - pos.y) * this.lerpFactor;
    pos.z += (clamped.z - pos.z) * this.lerpFactor;
    
    // Update reticle position (on ground, under content)
    if (this.reticleMesh) {
      this.reticleMesh.position.x = pos.x;
      this.reticleMesh.position.y = clamped.y + 0.01; // Match ground level from hit test
      this.reticleMesh.position.z = pos.z;
    }
    
    // Face camera
    this.faceCamera();
  }
  
  private getViewportDirectionXZ(): Vector3 | null {
    if (!this.scene) return null;
    const camera = this.scene.activeCamera;
    if (!camera) return null;
    const engine = this.scene.getEngine();
    const { x, y } = this.getScreenCoordsFromViewport(camera, engine, this.viewportX, this.viewportY);
    const pickRay = this.scene.createPickingRay(x, y, null, camera);
    const dirXZ = new Vector3(pickRay.direction.x, 0, pickRay.direction.z);
    if (dirXZ.lengthSquared() < 0.001) return null;
    dirXZ.normalize();
    return dirXZ;
  }

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────

  /** Raycast from viewport to ground plane */
  private raycastToGround(): Vector3 | null {
    if (!this.scene || !this.groundMesh) return null;
    
    const camera = this.scene.activeCamera;
    if (!camera) return null;
    
    const engine = this.scene.getEngine();
    
    const { x, y } = this.getScreenCoordsFromViewport(camera, engine, this.viewportX, this.viewportY);
    
    const hit = this.scene.pick(x, y, m => m === this.groundMesh, false, camera);
    
    if (hit?.hit && hit.pickedPoint) {
      return new Vector3(hit.pickedPoint.x, this.groundY, hit.pickedPoint.z);
    }
    
    return null;
  }

  private placeInFrontOfCamera(): void {
    const camera = this.scene?.activeCamera;
    if (!camera || !this.contentNode) return;
    
    const forward = camera.getDirection(Vector3.Forward());
    const pos = camera.position.add(forward.scale(this.distance));
    pos.y = this.groundY;
    
    this.contentNode.position.copyFrom(pos);
    this.faceCamera();
  }

  private clampDistance(position: Vector3): Vector3 {
    const camera = this.scene?.activeCamera;
    if (!camera) return position;
    
    const camXZ = new Vector3(camera.position.x, 0, camera.position.z);
    const posXZ = new Vector3(position.x, 0, position.z);
    const dist = Vector3.Distance(camXZ, posXZ);
    
    if (dist >= this.minDistance && dist <= this.maxDistance) {
      return position;
    }
    
    const clampedDist = Math.max(this.minDistance, Math.min(this.maxDistance, dist));
    const dir = posXZ.subtract(camXZ).normalize();
    const clampedXZ = camXZ.add(dir.scale(clampedDist));
    
    return new Vector3(clampedXZ.x, position.y, clampedXZ.z);
  }

  private faceCamera(): void {
    const camera = this.scene?.activeCamera;
    if (!camera || !this.contentNode) return;
    
    const dir = camera.position.subtract(this.contentNode.position);
    dir.y = 0;
    
    if (dir.lengthSquared() > 0.01) {
      dir.normalize();
      this.contentNode.rotation.y = Math.atan2(dir.x, dir.z);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Tap to Reposition
  // ─────────────────────────────────────────────────────────────

  /**
   * Reposition content at screen tap location
   * Only works when content is already placed
   * @param screenX Screen X coordinate
   * @param screenY Screen Y coordinate
   * @returns The new position, or null if reposition failed
   */
  public repositionAtScreenPoint(screenX: number, screenY: number): Vector3 | null {
    if (!this.scene || !this.contentNode) return null;
    if (this.state !== PlacementState.PLACED) return null;
    
    const camera = this.scene.activeCamera;
    if (!camera) return null;
    
    // Convert viewport client coordinates into canvas-local screen coordinates.
    const engine = this.scene.getEngine();
    const { x, y } = this.getScreenCoordsFromClientPoint(engine, screenX, screenY);

    // Create picking ray from converted screen point
    const pickRay = this.scene.createPickingRay(x, y, null, camera);
    
    // Calculate intersection with ground plane (y = groundY)
    if (Math.abs(pickRay.direction.y) < 0.0001) return null; // Ray is parallel to ground
    
    const t = (this.groundY - pickRay.origin.y) / pickRay.direction.y;
    if (t <= 0) return null; // Intersection is behind camera
    
    const targetPos = pickRay.origin.add(pickRay.direction.scale(t));
    targetPos.y = this.groundY;
    
    // Clamp to min/max distance
    const clampedPos = this.clampDistance(targetPos);
    
    // Move content
    this.contentNode.position.copyFrom(clampedPos);
    this.faceCamera();
    
    // Notify callback
    this.onReposition?.(clampedPos);
    
    return clampedPos;
  }

  /**
   * Set the reposition callback
   */
  public setOnReposition(callback: (position: Vector3) => void): void {
    this.onReposition = callback;
  }

  // ─────────────────────────────────────────────────────────────
  // Getters
  // ─────────────────────────────────────────────────────────────

  public getState(): PlacementState { return this.state; }
  public isPlacing(): boolean { return this.state === PlacementState.PLACING; }
  public isPlaced(): boolean { return this.state === PlacementState.PLACED; }
  public hasValidTarget(): boolean { return this.hasTarget; }
  public getContentNode(): TransformNode | undefined { return this.contentNode; }
  
  /**
   * Update gesture options at runtime (e.g., per-content settings)
   */
  public setGestureOptions(options: Partial<GestureOptions>): void {
    this.gestures?.setOptions(options);
  }

  /**
   * Configure gestures from a ContentInstance
   * Automatically reads arGestureOptions from each content item and sets up:
   * - Which nodes can rotate (those without disableRotation: true)
   * - Any other gesture options from content items
   */
  public configureFromContent(instance: ContentInstance): void {
    if (!instance?.isLoaded) return;
    
    const contentItems: ContentItem[] = instance.contentItem?.content || [];
    const rotatableNodes: TransformNode[] = [];
    
    // Find nodes that should rotate based on arGestureOptions
    // Match by path since assets load asynchronously and may be in different order
    for (const asset of instance.assets) {
      if (asset.type === 'model') {
        const modelAsset = asset as any;
        // Find the matching content item by path
        const contentItem = contentItems.find(c => c.path === asset.path);
        
        if (modelAsset.meshRoot) {
          // Add to rotatable if disableRotation is not set
          if (!contentItem?.arGestureOptions?.disableRotation) {
            rotatableNodes.push(modelAsset.meshRoot);
          }
          
          // Apply any other gesture options from the content item
          if (contentItem?.arGestureOptions) {
            const { disableRotation, ...otherOptions } = contentItem.arGestureOptions;
            if (Object.keys(otherOptions).length > 0) {
              this.gestures?.setOptions(otherOptions);
            }
          }
        }
      }
    }
    
    // Set which nodes should rotate (always call, even when empty - empty means no rotation)
    this.gestures?.setRotatableNodes(rotatableNodes);
  }

  // ─────────────────────────────────────────────────────────────
  // Interaction Target (gestures + tap-to-place)
  // ─────────────────────────────────────────────────────────────

  private interactionTarget?: HTMLElement;
  private tapHandler?: (e: Event) => void;
  private tapToPlaceEnabled = false;

  /**
   * Set the target element for all interactions (gestures + tap-to-place)
   * Defaults to canvas if no element provided
   * 
   * @param element - DOM element to receive touch/pointer events (e.g., DOM overlay or canvas)
   * @param options - { tapToPlace: boolean } - whether to enable tap-to-confirm during placement
   */
  public setInteractionTarget(element?: HTMLElement, options?: { tapToPlace?: boolean }): void {
    this.clearInteractionTarget();
    
    // Use provided element or fall back to canvas
    const target = element ?? this.scene?.getEngine().getRenderingCanvas() as HTMLElement;
    if (!target) return;
    
    this.interactionTarget = target;

    // Setup tap-to-place if enabled (default: true)
    const enableTap = options?.tapToPlace ?? true;
    if (enableTap) {
      this.tapToPlaceEnabled = true;
      this.tapHandler = (e: Event) => {
        // Ignore if tapping on a button
        const eventTarget = e.target as HTMLElement;
        if (eventTarget?.closest('button') || eventTarget?.tagName === 'BUTTON') {
          return;
        }
        
        if (this.isPlacing()) {
          this.confirmPlacement();
        }
      };
      target.addEventListener('touchstart', this.tapHandler, { passive: true });
    }
  }

  /** Clear interaction target and remove listeners */
  public clearInteractionTarget(): void {
    if (this.interactionTarget && this.tapHandler) {
      this.interactionTarget.removeEventListener('touchstart', this.tapHandler);
    }
    this.interactionTarget = undefined;
    this.tapHandler = undefined;
    this.tapToPlaceEnabled = false;
  }
  
  // ─────────────────────────────────────────────────────────────
  // Configuration
  // ─────────────────────────────────────────────────────────────

  public setGroundY(y: number): void {
    this.groundY = y;
    if (this.groundMesh) {
      this.groundMesh.position.y = y;
    }
  }
  public setDistance(d: number): void { this.distance = d; }
  public setMinDistance(d: number): void { this.minDistance = d; }
  public setMaxDistance(d: number): void { this.maxDistance = d; }

  private getScreenCoordsFromViewport(
    camera: { viewport?: { toGlobal: (width: number, height: number) => { x: number; y: number; width: number; height: number } } },
    engine: {
      getRenderWidth: (useScreen?: boolean) => number;
      getRenderHeight: (useScreen?: boolean) => number;
      getHardwareScalingLevel: () => number;
    },
    viewportX: number,
    viewportY: number
  ): { x: number; y: number } {
    const renderWidth = engine.getRenderWidth();
    const renderHeight = engine.getRenderHeight();
    const hardwareScale = engine.getHardwareScalingLevel();
    const viewport = camera.viewport?.toGlobal(renderWidth, renderHeight);
    if (viewport) {
      return {
        x: (viewport.x + viewport.width * viewportX) * hardwareScale,
        y: (viewport.y + viewport.height * viewportY) * hardwareScale,
      };
    }
    return {
      x: renderWidth * viewportX * hardwareScale,
      y: renderHeight * viewportY * hardwareScale,
    };
  }

  private getScreenCoordsFromClientPoint(
    engine: {
      getRenderingCanvas: () => HTMLCanvasElement | null;
    },
    screenX: number,
    screenY: number
  ): { x: number; y: number } {
    const canvas = engine.getRenderingCanvas();
    const rect = canvas?.getBoundingClientRect();

    if (!rect || rect.width <= 0 || rect.height <= 0) {
      return { x: screenX, y: screenY };
    }

    const canvasX = screenX - rect.left;
    const canvasY = screenY - rect.top;
    return {
      x: canvasX,
      y: canvasY,
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Cleanup
  // ─────────────────────────────────────────────────────────────

  public dispose(): void {
    this.clearInteractionTarget();
    this.gestures?.dispose();
    this.groundMesh?.material?.dispose();
    this.groundMesh?.dispose();
    this.groundMesh = undefined;
    this.reticleMesh?.material?.dispose();
    this.reticleMesh?.dispose();
    this.reticleMesh = undefined;
    this.contentNode?.dispose();
    this.contentNode = undefined;
    this.scene = undefined;
  }
}

export default PlacementController;
